import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { useMutation } from '@tanstack/react-query';
import HelpText from './HelpText';
import {
  loginOtpInitiate,
  loginOtpVerify,
  verifyUserToken,
} from '../../api/auth';
import { checkErrorTypeString } from '../../helpers/utils';
import UpLoadingBar from '../../components/Common/UpLoadingBar';
import LocalStorageHelper from '../../helpers/localStorageHelpers';
import UpLoadingCover from '../../components/Common/UpLoadingCover';
import UpToast from '../../components/Common/UpToast';
import { getRedirectRoute } from '../../helpers/bizDataHelpers';
import usePlatformRedirect from '../../helpers/usePlatformRedirect';
import PathStatus from '../../constants/PathStatus';
import { getRecaptchaSiteKey } from '../../helpers/environment.js';
import AnalyticsService from '../../services/analytics.js';
import Vent from '../../vent';
import CaptchaHelper from '../../helpers/CaptchaHelper.js';
import UpButton from '../../components/Common/UpButton/index.jsx';
import CardHeader from '../../components/CardHeader/index.jsx';
import UpInput from '../../components/Common/UpInput/index.jsx';

function TroubleLoginView() {
  const navigate = useNavigate();
  const captchaRef = useRef(null);
  const [searchParams] = useSearchParams();
  const [otp, setOtp] = useState('');
  const [inputError, setInputError] = useState('');
  const [resendCount, setResendCount] = useState(30);
  const [startCounter, setStartCounter] = useState(true);
  const { selectedLogin, isMobile, emailMobile, countryCode } =
    LocalStorageHelper.getUserDetails();

  const {
    isLoading: redirectLoading,
    redirect: callRedirectPlatform,
    atlasEnvPicker,
  } = usePlatformRedirect();

  useEffect(() => {
    if (startCounter && resendCount > 0) {
      setTimeout(() => setResendCount(resendCount - 1), 1000);
    }

    if (resendCount === 0) {
      setStartCounter(false);
      setResendCount(30);
    }
  }, [resendCount, startCounter]);

  const { isLoading: initiateLoading, mutate: callOtpInitiate } = useMutation({
    mutationFn: ({ authType, loginSource, params }) =>
      loginOtpInitiate(authType, loginSource, params),
    onError: onOtpInitiateError,
    onSuccess: onOtpInitiateSuccess,
  });

  useEffect(() => {
       const token = searchParams.get('token');
    if (token) {
      LocalStorageHelper.setAccessToken(token);
      callVerifyUserToken();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { isLoading: tokenLoading, mutate: callVerifyUserToken } = useMutation({
    mutationFn: verifyUserToken,
    onError: onVerifyUserError,
    onSuccess: onLoginSuccess,
  });

  function onVerifyUserError(err) {
    const errorMessage = checkErrorTypeString(err?.response?.data?.message);
    UpToast.error(errorMessage);
    navigate('/logout');
  }

  function onOtpInitiateSuccess(resp) {
    setOtp('');
    setInputError('');
    setStartCounter(true);
    UpToast.success('OTP sent successfully.');

    if (resp?.token) {
      LocalStorageHelper.setAccessToken(resp?.token);
    }
  }

  function onOtpInitiateError(err) {
    const errorMessage = checkErrorTypeString(err?.response?.data?.message);
    UpToast.error(errorMessage);
  }

  const { isLoading: verifyLoading, mutate: callOtpVerify } = useMutation({
    mutationFn: ({ authType, loginSource, params }) =>
      loginOtpVerify(authType, loginSource, params),
    onSuccess: onLoginSuccess,
    onError: err => {
      const errorMessage = checkErrorTypeString(err?.response?.data?.message);

      if (err?.response?.data?.err_text === 'INVALID_RECAPTCHA') {
        UpToast.error(errorMessage);
      } else {
        setInputError(errorMessage);
      }
    },
  });

  function onLoginSuccess(resp) {
    Vent.onUserIdentified();
    AnalyticsService.onUleLogin({
      field: isMobile ? 'phone' : 'email',
      otp: true,
      [isMobile ? 'phone' : 'email']: emailMobile || '',
      isd_code: countryCode,
    });

    const redirect = getRedirectRoute(resp);
    const allBizData = LocalStorageHelper.getBizData();

    if (
      allBizData?.length === 1 &&
      allBizData?.[0]?.appsbiz?.length === 1 &&
      resp?.path_status === PathStatus.APP_BIZ_SELECTION
    ) {
      const bizData = allBizData[0];
      callRedirectPlatform({
        appType: bizData?.appsbiz?.[0]?.app_name,
        params: {
          biz_id: bizData?.biz_id,
          app_name: bizData?.appsbiz?.[0]?.app_name,
          app_biz_id: bizData?.appsbiz?.[0]?.app_biz_id,
          redirect_host: LocalStorageHelper.getRedirectHost(),
        },
      });
      return;
    }

    if (redirect) {
      navigate(redirect);
    }
  }

  function onOtpChange(updatedValue) {
    setInputError('');
    setOtp(updatedValue);
  }

  async function onResendOtp() {
    const captchaToken = await CaptchaHelper.getToken(captchaRef);
    if (!captchaToken) {
      return;
    }

    const params = {
      value: emailMobile,
      type: isMobile ? 'phone' : 'email',
      isd_code: isMobile ? countryCode : '',
      re_captcha: captchaToken,
    };

    callOtpInitiate({
      params,
      authType: 'otp',
      loginSource: isMobile ? 'phone' : 'email',
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (!otp || viewLoading) {
      return;
    }
    try {
      const captchaToken = await CaptchaHelper.getToken(captchaRef);
      if (!captchaToken) {
        return;
      }

      const params = {
        otp,
        value: emailMobile,
        type: isMobile ? 'phone' : 'email',
        isd_code: isMobile ? countryCode : '',
        re_captcha: captchaToken,
      };

      callOtpVerify({
        params,
        authType: 'otp',
        loginSource: isMobile ? 'phone' : 'email',
      });
    } catch (err) {
      console.log(err)
    }
  }

  const viewLoading =
    initiateLoading || verifyLoading || tokenLoading || redirectLoading;

  return (
    <div>
      {atlasEnvPicker}
      <UpLoadingBar loading={viewLoading} />
      <div className="neutral-view-wrapper relative">
        <UpLoadingCover loading={viewLoading} />
        <div>
          <CardHeader
            title="Have trouble logging in?"
            subtitle={
              <div className="flex items-center">
                {selectedLogin && (
                  <div>
                    {isMobile ? (
                      <div className="flex items-center">
                        <span className="font-normal mr-1">
                          We have sent you an OTP to log you in with{' '}
                        </span>
                        <span>{selectedLogin}</span>
                        <UpButton
                          variant="link"
                          loading={viewLoading}
                          onClick={() => navigate('/login/email-mobile')}>
                          Change
                        </UpButton>
                      </div>
                    ) : (
                      <span className="mr-1">
                        <span className="font-normal">
                          We have sent you an OTP to log you in with{' '}
                        </span>

                        <div className="flex items-center">
                          <span>{selectedLogin}</span>
                          <UpButton
                            variant="link"
                            loading={viewLoading}
                            onClick={() => navigate('/login/email-mobile')}>
                            Change
                          </UpButton>
                        </div>
                      </span>
                    )}
                  </div>
                )}
              </div>
            }
          />
          <form onSubmit={handleSubmit}>
            <UpInput
              autoFocus
              loading={viewLoading}
              type="number"
              name="otp"
              label="Enter OTP"
              placeholder="xxx-xxx"
              wrapperClass="mt-12 mb-8"
              value={otp}
              onChange={onOtpChange}
              errorContent={inputError}
              extraAction={
                startCounter ? (
                  <div className="text-up-clr-dark-grey text-sm text-right">
                    Resend OTP in{' '}
                    <span className="font-semibold">
                      00:{('0' + resendCount).slice(-2)}
                    </span>
                  </div>
                ) : (
                  <UpButton
                    loading={viewLoading}
                    variant="link"
                    onClick={onResendOtp}
                    wrapperClass="w-max ml-auto">
                    Resend OTP
                  </UpButton>
                )
              }
            />{' '}
            <UpButton type="submit" wrapperClass="mb-5" loading={viewLoading}>
              Login
            </UpButton>
          </form>
          <div>
            <hr className="my-4" />
            <UpButton
              isSecondaryStyled
              loading={viewLoading}
              onClick={() => navigate('/login/password')}>
              Login with Password instead
            </UpButton>
          </div>
        </div>
        <HelpText screen="trouble-login" prefilled={{ loginCred: emailMobile }} />
      </div>

      <ReCAPTCHA
        ref={captchaRef}
        size="invisible"
        badge="bottomleft"
        sitekey={getRecaptchaSiteKey()}
      />
    </div>
  );
}

export default TroubleLoginView;
